import {
	bucketsOfItem,
	LINKEDIN_SIZE_BUCKETS,
	type SizeBucket,
	type ScoreSizeItem as SizeItem,
} from "@signalist/shared-types";

import { ScoreCriteriaType } from "@/pages/Private/pages/Score/schema/score.schema";

import { Tier, TIERS } from "./criteriaTiers";

export { LINKEDIN_SIZE_BUCKETS, type SizeBucket } from "@signalist/shared-types";

/**
 * Company size as LinkedIn's nine fixed headcount ranges instead of free numeric
 * bounds. The rubric still stores `{ greaterEqual, lowerEqual }` — the buckets
 * are the *editing* shape, so the scoring engine is untouched — but a first-time
 * user picks from the ranges they already know from LinkedIn rather than
 * inventing boundaries, and the ranges can't overlap or leave gaps.
 *
 * A bucket lives in exactly one tier: selecting it in another tier moves it.
 */
/** Mirrors NumericValueType in the API — the rubric stores which bounds are set. */
const numericType = (bucket: SizeBucket): string =>
	bucket.lowerEqual === undefined ? "GREATER_THAN" : "BETWEEN";

const sizeGroup = (criteria: ScoreCriteriaType): { active?: boolean; items?: SizeItem[] } =>
	(
		criteria.criteria.companyScoring as unknown as {
			EMPLOYEE_SIZE?: { active?: boolean; items?: SizeItem[] };
		}
	).EMPLOYEE_SIZE ?? {};

export type SizeTiers = Record<Tier, string[]>;

const emptyTiers = (): SizeTiers => ({ 3: [], 2: [], 1: [], 0: [] });

/** Reads the stored ranges back as bucket labels per tier. */
export const readSizeTiers = (criteria: ScoreCriteriaType): SizeTiers => {
	const tiers = emptyTiers();
	const claimed = new Set<string>();

	for (const item of sizeGroup(criteria).items ?? []) {
		const tier = TIERS.find((entry) => entry === item.points);

		if (tier === undefined) {
			continue;
		}

		for (const label of bucketsOfItem(item)) {
			// A bucket belongs to one tier — the first item that claims it wins, so
			// overlapping legacy ranges cannot show the same bucket twice.
			if (claimed.has(label)) {
				continue;
			}

			claimed.add(label);
			tiers[tier].push(label);
		}
	}

	return tiers;
};

export const isSizeCriterionActive = (criteria: ScoreCriteriaType): boolean =>
	sizeGroup(criteria).active === true;

const bucketByLabel = (label: string): SizeBucket | undefined =>
	LINKEDIN_SIZE_BUCKETS.find((bucket) => bucket.label === label);

const withSizeGroup = (
	criteria: ScoreCriteriaType,
	patch: { active?: boolean; items?: SizeItem[] }
): ScoreCriteriaType =>
	({
		...criteria,
		criteria: {
			...criteria.criteria,
			companyScoring: {
				...criteria.criteria.companyScoring,
				EMPLOYEE_SIZE: { ...sizeGroup(criteria), ...patch },
			},
		},
	}) as ScoreCriteriaType;

/** Writes the tier map back as one numeric range item per selected bucket. */
export const writeSizeTiers = (
	criteria: ScoreCriteriaType,
	tiers: SizeTiers
): ScoreCriteriaType => {
	const items: SizeItem[] = [];

	for (const tier of TIERS) {
		for (const label of tiers[tier]) {
			const bucket = bucketByLabel(label);

			if (!bucket) {
				continue;
			}

			items.push({
				points: tier,
				greaterEqual: bucket.greaterEqual,
				lowerEqual: bucket.lowerEqual,
				type: numericType(bucket),
				description: label,
			});
		}
	}

	return withSizeGroup(criteria, { items });
};

export const setSizeCriterionActive = (
	criteria: ScoreCriteriaType,
	active: boolean
): ScoreCriteriaType => withSizeGroup(criteria, { active });

/**
 * The ranges no tier claims (PREC-3896). They score nothing — which is a
 * legitimate choice and also the easiest one to make by accident, because
 * assigning the four ranges a user recognises leaves the other five silently
 * worth zero points. The screen names them instead of stating the rule.
 */
export const unassignedSizeBuckets = (tiers: SizeTiers): string[] => {
	const claimed = new Set(TIERS.flatMap((tier) => tiers[tier]));

	return LINKEDIN_SIZE_BUCKETS.filter((bucket) => !claimed.has(bucket.label)).map(
		(bucket) => bucket.label
	);
};

/** Moves a bucket into `tier`, or out of it when it is already there. */
export const toggleSizeBucket = (tiers: SizeTiers, tier: Tier, label: string): SizeTiers => {
	const next = emptyTiers();

	for (const entry of TIERS) {
		next[entry] = tiers[entry].filter((value) => value !== label);
	}

	if (!tiers[tier].includes(label)) {
		next[tier] = [...next[tier], label].sort(
			(a, b) =>
				LINKEDIN_SIZE_BUCKETS.findIndex((bucket) => bucket.label === a) -
				LINKEDIN_SIZE_BUCKETS.findIndex((bucket) => bucket.label === b)
		);
	}

	return next;
};

/**
 * The CEO split stores a plain "more than N employees" cutoff, but the rest of
 * the scoring is defined in LinkedIn's ranges — so the cutoff is picked as the
 * first range that is disqualified, and stored as the headcount just below it.
 * "Self-employed" is left out: a one-person company has no split to make.
 */
export const CEO_CUTOFF_BUCKETS: SizeBucket[] = LINKEDIN_SIZE_BUCKETS.slice(1);

export const cutoffBelowBucket = (bucket: SizeBucket): number => bucket.greaterEqual - 1;

/**
 * How a cutoff bucket reads inside a sentence: where the range *starts*, not the
 * range itself. The picker sits in "Disqualify CEOs & MDs at companies of __
 * employees and up", and "501-1,000 employees and up" contradicts itself — the
 * upper bound is precisely what "and up" overrides.
 */
export const cutoffBucketLabel = (bucket: SizeBucket): string =>
	bucket.greaterEqual.toLocaleString("en-US");

/** The first range that a stored cutoff disqualifies, for reading a cutoff back. */
export const bucketAboveCutoff = (cutoff: number): SizeBucket =>
	CEO_CUTOFF_BUCKETS.find((bucket) => bucket.greaterEqual > cutoff) ??
	CEO_CUTOFF_BUCKETS[CEO_CUTOFF_BUCKETS.length - 1];

/** Which tier a bucket currently sits in, if any. */
export const tierOfBucket = (tiers: SizeTiers, label: string): Tier | null =>
	TIERS.find((tier) => tiers[tier].includes(label)) ?? null;
